// Volunteer Check-In Component for AAR Guest Management System
import React, { useState, useEffect, useRef } from 'react';
import { fetchParticipants, checkInVolunteers, fetchTodayVolunteerCheckIns } from '../firebase/firestoreService';
import { getCurrentUser } from '../firebase/authService';
import { getVolunteerServiceType } from '../utils/dateUtils';
import { getServiceType, VOLUNTEER_GROUP_ID } from '../utils/roles';
import { logAction, logError, LOG_CATEGORY, LOG_STATUS } from '../firebase/activityLog';
import { useAppBusy } from '../utils/appUpdate';
import placeholderAvatar from '../assets/placeholder-avatar.svg';
import '../styles/ConfirmDialog.css';
import '../styles/VolunteerCheckIn.css';
import { readOnlyProps } from '../utils/viewAs';

const VolunteerCheckIn = () => {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const [volunteers, setVolunteers] = useState([]);
  const [selectedVolunteers, setSelectedVolunteers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [volunteerNotes, setVolunteerNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [todayCheckIns, setTodayCheckIns] = useState([]);
  const [checkedInVolunteerIds, setCheckedInVolunteerIds] = useState(new Set());
  const [showDropdown, setShowDropdown] = useState(false);
  // Result of the last submit. Rendered as a modal the operator must dismiss,
  // rather than a banner that cleared itself after 3 seconds — an operator
  // looking at the submit button at the bottom of a desktop page could miss
  // the old banner entirely and conclude the check-in had not worked.
  const [checkInResult, setCheckInResult] = useState(null);
  // Re-entrancy guard: blocks a second submit before React has re-rendered the
  // disabled button (rapid double-click / Enter key). Mirrors the guest
  // check-in guard added in v1.2.76.
  const submitInProgressRef = useRef(false);
  const user = getCurrentUser();

  // Auto-detect service type from current time
  const detectedServiceType = getVolunteerServiceType();
  const detectedServiceInfo = detectedServiceType ? getServiceType(detectedServiceType) : null;
  // The service a check-in submitted right now would be recorded under. Used to
  // decide whether a volunteer is already checked in for THIS service (a
  // volunteer may legitimately be checked in for an earlier service too).
  const currentServiceTarget = detectedServiceType || 'unscheduled';

  // Volunteer IDs already checked in for the current service today. These are
  // blocked from re-selection; the service layer enforces the same rule.
  const checkedInForCurrentService = new Set(
    todayCheckIns
      .filter(c => c.serviceType === currentServiceTarget)
      .map(c => c.volunteerId)
  );

  // Hold off the idle auto-reload while there is work in progress: an in-flight
  // submit, a selection not yet checked in, or a result the operator has not
  // acknowledged. A reload at any of those moments would lose the operator's
  // place — or worse, look like the check-in failed.
  useAppBusy(
    'volunteer-checkin',
    submitting || selectedVolunteers.length > 0 || checkInResult !== null
  );

  // Scroll to top when an inline error appears. The submit result no longer
  // relies on this — it is a modal, which is visible wherever the page is
  // scrolled to and on any viewport size.
  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  useEffect(() => {
    loadVolunteers();
    loadTodayCheckIns();
  }, []);

  // Log a search that settles on no matches. A volunteer missing from the
  // roster (not in the CCB group, or inactive) otherwise just looks like the
  // page doing nothing. Waits for typing to pause so each keystroke isn't
  // logged.
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2 || loading || volunteers.length === 0) return;
    if (getFilteredDropdownVolunteers().length > 0) return;
    const timer = setTimeout(() => {
      logAction(
        LOG_CATEGORY.VOLUNTEER_CHECKIN,
        'volunteer_search_no_match',
        LOG_STATUS.INFO,
        `Volunteer search "${term}" matched no one`,
        { searchTerm: term, rosterSize: volunteers.length }
      );
    }, 1500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, loading, volunteers]);

  const loadVolunteers = async () => {
    try {
      setLoading(true);
      setError('');
      const participantList = await fetchParticipants(VOLUNTEER_GROUP_ID);
      if (!participantList || participantList.length === 0) {
        setError('No participants found. Please wait for the daily sync or contact an administrator.');
        setVolunteers([]);
        logAction(
          LOG_CATEGORY.VOLUNTEER_CHECKIN,
          'volunteer_roster_empty',
          LOG_STATUS.FAILURE,
          'Volunteer Check-In opened with an empty roster',
          { serviceType: currentServiceTarget }
        );
      } else {
        setVolunteers(participantList);
        // Record that the page was reached at all. Without this, an operator
        // who never gets as far as submitting leaves no trace, and a report of
        // "couldn't check in volunteers" can't be told apart from never
        // opening the page.
        logAction(
          LOG_CATEGORY.VOLUNTEER_CHECKIN,
          'volunteer_checkin_opened',
          LOG_STATUS.INFO,
          `Opened Volunteer Check-In (${participantList.length} volunteers, ${currentServiceTarget})`,
          { serviceType: currentServiceTarget, rosterSize: participantList.length }
        );
      }
    } catch (err) {
      console.error('Error loading participants:', err);
      setError(`Failed to load volunteer list: ${err.message || 'Please try again.'}`);
      setVolunteers([]);
      logError(
        LOG_CATEGORY.VOLUNTEER_CHECKIN,
        'volunteer_roster_load_failed',
        err,
        'Failed to load the volunteer roster',
        { serviceType: currentServiceTarget }
      );
    } finally {
      setLoading(false);
    }
  };

  const loadTodayCheckIns = async () => {
    try {
      const checkIns = await fetchTodayVolunteerCheckIns();
      setTodayCheckIns(checkIns);
      const ids = new Set(checkIns.map(c => c.volunteerId));
      setCheckedInVolunteerIds(ids);
    } catch (err) {
      console.error('Error loading today\'s volunteer check-ins:', err);
    }
  };

  const getSortedVolunteers = () => {
    return [...volunteers].sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  const getFilteredDropdownVolunteers = () => {
    const sorted = getSortedVolunteers();
    // Exclude already selected volunteers from dropdown
    const selectedIds = new Set(selectedVolunteers.map(v => v.id));
    const available = sorted.filter(v => !selectedIds.has(v.id));

    if (searchTerm.trim() === '') {
      return available;
    }
    const term = searchTerm.toLowerCase();
    return available.filter(v =>
      v.firstName.toLowerCase().includes(term) ||
      v.lastName.toLowerCase().includes(term) ||
      `${v.firstName} ${v.lastName}`.toLowerCase().includes(term)
    );
  };

  const handleSelectVolunteer = (volunteer) => {
    // Block re-selecting someone already checked in for the current service.
    if (checkedInForCurrentService.has(volunteer.id)) {
      const existing = todayCheckIns.find(
        c => c.volunteerId === volunteer.id && c.serviceType === currentServiceTarget
      );
      logAction(
        LOG_CATEGORY.VOLUNTEER_CHECKIN,
        'volunteer_selection_blocked',
        LOG_STATUS.INFO,
        `Blocked selecting ${volunteer.firstName} ${volunteer.lastName}: already checked in for ${currentServiceTarget}`,
        {
          serviceType: currentServiceTarget,
          volunteerId: volunteer.id,
          checkedInBy: existing?.checkInByName || null
        }
      );
      setError(`${volunteer.firstName} ${volunteer.lastName} is already checked in for this service.`);
      setSearchTerm('');
      setShowDropdown(false);
      return;
    }
    if (!selectedVolunteers.find(v => v.id === volunteer.id)) {
      setSelectedVolunteers([...selectedVolunteers, volunteer]);
    }
    setSearchTerm('');
    setShowDropdown(false);
    setError('');
  };

  const handleRemoveVolunteer = (volunteerId) => {
    setSelectedVolunteers(selectedVolunteers.filter(v => v.id !== volunteerId));
    setVolunteerNotes(prev => {
      const updated = { ...prev };
      delete updated[volunteerId];
      return updated;
    });
  };

  const handleNoteChange = (volunteerId, value) => {
    if (value.length <= 100) {
      setVolunteerNotes(prev => ({ ...prev, [volunteerId]: value }));
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();

    if (selectedVolunteers.length === 0) {
      setError('Please select at least one volunteer.');
      return;
    }

    // Non-re-entrant: a second submit while the first is in flight is dropped.
    if (submitInProgressRef.current) return;
    submitInProgressRef.current = true;

    // Use auto-detected service type, or 'unscheduled' for off-hours/off-days
    const serviceType = getVolunteerServiceType() || 'unscheduled';
    const attempted = selectedVolunteers.map(v => ({
      id: v.id,
      name: `${v.firstName} ${v.lastName}`
    }));
    const startedAt = Date.now();

    try {
      setSubmitting(true);
      setError('');

      logAction(
        LOG_CATEGORY.VOLUNTEER_CHECKIN,
        'volunteer_check_in_submitted',
        LOG_STATUS.INFO,
        `Submitting ${attempted.length} volunteer check-in(s) for ${serviceType}`,
        { serviceType, volunteers: attempted }
      );

      const result = await checkInVolunteers({
        volunteers: selectedVolunteers.map(v => ({
          id: v.id,
          firstName: v.firstName,
          lastName: v.lastName,
          notes: volunteerNotes[v.id] || ''
        })),
        serviceType,
        checkInBy: user.uid,
        checkInByName: user.displayName || user.email
      });

      const skipped = result?.skipped || [];
      const failed = result?.failed || [];
      const handledIds = new Set([...skipped, ...failed].map(x => x.id));
      const checkedIn = selectedVolunteers
        .filter(v => !handledIds.has(v.id))
        .map(v => ({ id: v.id, name: `${v.firstName} ${v.lastName}` }));

      // Always reconcile against the server before reporting, so what the
      // operator is told matches what is actually stored.
      await loadTodayCheckIns();

      setCheckInResult({ serviceType, checkedIn, skipped, failed });

      logAction(
        LOG_CATEGORY.VOLUNTEER_CHECKIN,
        'volunteer_check_in_result',
        failed.length > 0
          ? (checkedIn.length > 0 ? LOG_STATUS.PARTIAL : LOG_STATUS.FAILURE)
          : LOG_STATUS.SUCCESS,
        `Checked in ${checkedIn.length}, already checked in ${skipped.length}, failed ${failed.length}`,
        {
          serviceType,
          durationMs: Date.now() - startedAt,
          checkedIn,
          skipped,
          failed
        }
      );

      // Keep only the ones that did not land, so a retry re-submits exactly
      // those. Everything resolved drops out of the selection.
      const failedIds = new Set(failed.map(f => f.id));
      setSelectedVolunteers(prev => prev.filter(v => failedIds.has(v.id)));
      setVolunteerNotes(prev => {
        const next = {};
        for (const [id, note] of Object.entries(prev)) {
          if (failedIds.has(id)) next[id] = note;
        }
        return next;
      });
    } catch (err) {
      console.error('Volunteer check-in error:', err);

      // The batch threw outright. Some volunteers may still have been written
      // before it failed, so re-read from the server and report what is
      // actually recorded rather than declaring a blanket failure.
      await loadTodayCheckIns();

      setCheckInResult({
        serviceType,
        checkedIn: [],
        skipped: [],
        failed: attempted.map(a => ({
          ...a,
          code: err?.code || 'unknown',
          message: err?.message || 'Check-in failed'
        })),
        fatal: true
      });

      logError(
        LOG_CATEGORY.VOLUNTEER_CHECKIN,
        'volunteer_check_in_failed',
        err,
        'Volunteer check-in threw before any per-volunteer result was returned',
        { serviceType, durationMs: Date.now() - startedAt, volunteers: attempted }
      );
    } finally {
      setSubmitting(false);
      submitInProgressRef.current = false;
    }
  };

  const dismissCheckInResult = () => {
    setCheckInResult(null);
  };

  // Format check-in time for display (day, date, time)
  const formatCheckInTimeParts = (checkIn) => {
    if (!checkIn.checkInTime) return { day: '', date: '', time: '' };
    const d = checkIn.checkInTime.toDate ? checkIn.checkInTime.toDate() : new Date(checkIn.checkInTime);
    return {
      day: d.toLocaleDateString('en-US', { weekday: 'short' }),
      date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };
  };

  // Get service label for display
  const getServiceLabel = (serviceId) => {
    const info = getServiceType(serviceId);
    if (info) return info.name;
    if (serviceId === 'unscheduled') return 'Off-schedule';
    return serviceId || '';
  };

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading...</p></div>
    );
  }

  return (
    <div className="volunteer-checkin-container">
      <div className="volunteer-checkin-header">
        <h1 className="volunteer-checkin-title">Volunteer Check-In</h1>
        <div className="volunteer-date-display">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
        <div className="service-indicator">
          {detectedServiceInfo
            ? <span className="service-badge active">{detectedServiceInfo.name}</span>
            : <span className="service-badge off-hours">Off-schedule check-in</span>
          }
        </div>
      </div>

      {error && (
        <div className="message error-message">{error}</div>
      )}

      <div className="volunteer-checkin-content">
        {/* Volunteer Selection */}
        <div className="section">
          <div className="volunteer-selection-header">
            <h2>Select Volunteers</h2>
            {selectedVolunteers.length > 0 && (
              <span className="selected-count">
                {selectedVolunteers.length} selected
              </span>
            )}
          </div>

          <div className="search-box search-wrapper">
            <input
              type="text"
              placeholder="Search for a volunteer by name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(e.target.value.trim() !== '');
              }}
              // The list only opens once a name is typed. Opening the full
              // roster on a bare click laid it over the selected volunteers and
              // the Check In button, which an operator adding people one by one
              // (click box, pick, click box again) then never saw.
              onClick={() => setShowDropdown(searchTerm.trim() !== '')}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              className="search-input"
            />
            {searchTerm && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={() => {
                  setSearchTerm('');
                  setShowDropdown(false);
                }}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
            {showDropdown && searchTerm.trim() !== '' && getFilteredDropdownVolunteers().length > 0 && (
              <div className="search-dropdown">
                {getFilteredDropdownVolunteers().map((volunteer, index) => {
                  const blockedForService = checkedInForCurrentService.has(volunteer.id);
                  return (
                  <div
                    key={volunteer.id || index}
                    className={`dropdown-item ${blockedForService ? 'dropdown-item-disabled' : ''}`}
                    onClick={() => handleSelectVolunteer(volunteer)}
                  >
                    <div className="dropdown-guest-image">
                      {volunteer.image ? (
                        <img src={volunteer.image} alt={`${volunteer.firstName} ${volunteer.lastName}`} />
                      ) : (
                        <img src={placeholderAvatar} alt={`${volunteer.firstName} ${volunteer.lastName}`} className="dropdown-guest-avatar-img" />
                      )}
                    </div>
                    <span className="dropdown-guest-name">
                      {volunteer.firstName} {volunteer.lastName}
                    </span>
                    {blockedForService ? (
                      <span className="dropdown-checkin-badge">Checked In</span>
                    ) : checkedInVolunteerIds.has(volunteer.id) && (
                      <span className="dropdown-checkin-badge">Earlier Service</span>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
            {showDropdown && searchTerm.trim() !== '' && getFilteredDropdownVolunteers().length === 0 && (
              <div className="search-dropdown">
                <div className="search-dropdown-empty">
                  No volunteers match "{searchTerm.trim()}". They may not be on the AAR volunteer roster in CCB.
                </div>
              </div>
            )}
          </div>

          {/* Selected Volunteers */}
          {selectedVolunteers.length > 0 && (
            <div className="selected-volunteers">
              <h3>Selected for Check-In</h3>
              <div className="selected-volunteer-list">
                {selectedVolunteers.map(volunteer => (
                  <div key={volunteer.id} className="selected-volunteer-chip">
                    <div className="chip-top-row">
                      <div className="chip-image">
                        {volunteer.image ? (
                          <img src={volunteer.image} alt={`${volunteer.firstName} ${volunteer.lastName}`} />
                        ) : (
                          <img src={placeholderAvatar} alt={`${volunteer.firstName} ${volunteer.lastName}`} />
                        )}
                      </div>
                      <span className="chip-name">{volunteer.firstName} {volunteer.lastName}</span>
                      <button
                        className="chip-remove-btn"
                        onClick={() => handleRemoveVolunteer(volunteer.id)}
                        title="Remove from selection"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="chip-note-row">
                      <input
                        type="text"
                        className="chip-note-input"
                        placeholder="Add a note (optional)"
                        value={volunteerNotes[volunteer.id] || ''}
                        onChange={(e) => handleNoteChange(volunteer.id, e.target.value)}
                        maxLength={100}
                      />
                      {(volunteerNotes[volunteer.id] || '').length > 0 && (
                        <span className="chip-note-count">
                          {(volunteerNotes[volunteer.id] || '').length}/100
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Submit — pinned to the bottom of the window while anything is
            selected, so neither a short screen nor the search list can hide
            it. An AAR leader on a 781px-tall window could select volunteers
            but never saw the button below the fold. */}
        {selectedVolunteers.length > 0 && (
          <form onSubmit={handleCheckIn} className="volunteer-checkin-form volunteer-checkin-actionbar">
            <div className="form-actions">
              <span className="actionbar-count">
                {selectedVolunteers.length} selected
              </span>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setSelectedVolunteers([]);
                  setVolunteerNotes({});
                }}
                disabled={submitting}
              >
                Clear Selection
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || selectedVolunteers.length === 0 || ro.disabled}
                title={ro.title}
              >
                {submitting
                  ? 'Checking In...'
                  : `Check In ${selectedVolunteers.length} Volunteer${selectedVolunteers.length > 1 ? 's' : ''}`
                }
              </button>
            </div>
          </form>
        )}

        {/* Today's Check-Ins */}
        {todayCheckIns.length > 0 && (
          <div className="section">
            <h2>Today's Volunteer Check-Ins ({todayCheckIns.length})</h2>
            <div className="today-header-row">
              <span className="today-col-name">Volunteer</span>
              <span className="today-col-notes">Notes</span>
              <span className="today-col-time">Time</span>
            </div>
            <div className="today-checkins-list">
              {todayCheckIns.map(checkIn => (
                <div key={checkIn.id} className="today-checkin-row">
                  <div className="today-col-name">
                    <div className="checkin-status-dot"></div>
                    <span className="today-checkin-name">
                      {checkIn.volunteerFirstName} {checkIn.volunteerLastName}
                    </span>
                    {checkIn.serviceType && checkIn.serviceType !== 'unscheduled' && (
                      <span className="today-checkin-service">{getServiceLabel(checkIn.serviceType)}</span>
                    )}
                  </div>
                  <div className="today-col-notes">
                    {checkIn.notes ? (
                      <span className="today-note-text" title={checkIn.notes}>{checkIn.notes}</span>
                    ) : (
                      <span className="today-note-empty">--</span>
                    )}
                  </div>
                  <div className="today-col-time">
                    {(() => {
                      const t = formatCheckInTimeParts(checkIn);
                      return (
                        <>
                          <span className="today-time-day">{t.day}</span>
                          <span className="today-time-date">{t.date}</span>
                          <span className="today-time-clock">{t.time}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit result — a modal the operator must dismiss. This replaced a
          banner that auto-cleared after 3 seconds, which on a desktop appeared
          far above the submit button and was routinely missed, leaving the
          operator to believe a successful check-in had failed. */}
      {checkInResult && (
        <div
          className="confirm-dialog-overlay"
          role="dialog"
          aria-modal="true"
          onClick={dismissCheckInResult}
        >
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-dialog-title">
              {checkInResult.failed.length > 0
                ? (checkInResult.checkedIn.length > 0 ? 'Partly checked in' : 'Check-in failed')
                : checkInResult.checkedIn.length > 0
                  ? 'Volunteers checked in'
                  : 'Already checked in'}
            </h3>
            <div className="confirm-dialog-body">
              {checkInResult.checkedIn.length > 0 && (
                <div className="checkin-result-group checkin-result-success">
                  <div className="checkin-result-heading">
                    ✓ Checked in ({checkInResult.checkedIn.length})
                  </div>
                  <div className="checkin-result-names">
                    {checkInResult.checkedIn.map(v => v.name).join(', ')}
                  </div>
                </div>
              )}

              {checkInResult.skipped.length > 0 && (
                <div className="checkin-result-group checkin-result-info">
                  <div className="checkin-result-heading">
                    ● Already checked in ({checkInResult.skipped.length})
                  </div>
                  <div className="checkin-result-names">
                    {checkInResult.skipped.map(v => v.name).join(', ')}
                  </div>
                  <div className="checkin-result-note">
                    No action needed — their check-in for this service is already recorded.
                  </div>
                </div>
              )}

              {checkInResult.failed.length > 0 && (
                <div className="checkin-result-group checkin-result-error">
                  <div className="checkin-result-heading">
                    ✕ Not checked in ({checkInResult.failed.length})
                  </div>
                  <div className="checkin-result-names">
                    {checkInResult.failed.map(v => v.name).join(', ')}
                  </div>
                  <div className="checkin-result-note">
                    {navigator.onLine === false
                      ? 'This device is offline. Reconnect and try again — they are still selected below.'
                      : 'They are still selected below, so you can try again. Check the list of today\u2019s check-ins first in case the record went through.'}
                  </div>
                </div>
              )}
            </div>
            <div className="confirm-dialog-actions">
              <button
                type="button"
                className="btn btn-primary"
                autoFocus
                onClick={dismissCheckInResult}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolunteerCheckIn;
